import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { isRestricted, isEmailUnverified, isAgeCleared } from "@/constants/ageContract";

const AuthLoading = () => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background" data-testid="auth-loading">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
    <p className="text-sm text-muted-foreground">Konto wird geprüft …</p>
  </div>
);

// `social`-Routen (Discover/Reisen/Matches/Nachrichten/Fremdprofile) verlangen einen
// vollständig freigeschalteten Zustand. Verbindliche Priorität (Quelle: /auth/me):
// 1) E-Mail unbestätigt -> /e-mail-bestaetigen  2) Alterssperre -> /altersverifikation.
export default function ProtectedRoute({ children, social = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Nur wenn /auth/me eine Altersrestriktion meldet, die autoritative Wahrheit aus
  // GET /verification/age (gleicher Query-Key wie AgeVerificationPage -> dedupliziert)
  // gegenprüfen. So wird ein bereits verifizierter Account nicht wegen eines veralteten
  // /auth/me-Feldes zurück auf /altersverifikation geschickt.
  const ageRestricted = social && !!user && isRestricted(user);
  const { data: ageStatus, isLoading: ageLoading } = useQuery({
    queryKey: ["verificationAge"],
    queryFn: async () => (await api.get("/verification/age")).data,
    enabled: ageRestricted,
    retry: false,
    staleTime: 30000,
  });

  if (loading) return <AuthLoading />;
  if (!user) return <Navigate to="/" replace />;
  // Zentrale Anzeigenamen-Weiche: Bestandsnutzer ohne verwendbaren Namen (z. B. alte
  // Google-Konten ohne first_name/display_name/name) werden einmalig zur Namenseingabe
  // nach /complete-profile geleitet. first_name bleibt kanonisch; display_name/name nur
  // als Erkennung. Schleifensicher: auf /complete-profile selbst wird NICHT umgeleitet.
  const hasUsableName = Boolean(user.first_name || user.display_name || user.name);
  if (!hasUsableName && location.pathname !== "/complete-profile") {
    return <Navigate to="/complete-profile" replace />;
  }
  if (social) {
    if (isEmailUnverified(user)) return <Navigate to="/e-mail-bestaetigen" replace />;
    if (ageRestricted) {
      // Autoritativen Status erst abwarten (kein voreiliger Redirect / kein voreiliger Zugriff).
      if (ageLoading) return <AuthLoading />;
      if (!isAgeCleared(user, ageStatus)) return <Navigate to="/altersverifikation" replace />;
    }
  }
  return children;
}
